import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Center, Divider, Stack, Text } from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AddButtonLink, Content, TextInputControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { TechnologicalTransferFoundModal } from '../TechnologicalTransfer/TechnologicalTransferFoundModal';
import { useReclaimAuthorship } from '../TechnologicalTransfer/model/TechnologicalTransferQuery';
import {
  ReclaimRequest,
  TechnologicalTransferReclaimSearch,
} from '../TechnologicalTransfer/model/TechnologicalTransferTypes';
import { useSearchTechnologicalTransferByIdentifiers } from './model/TechnologicalTransferQuery';
import { TechnologicalTransferSearchRequest } from './model/TechnologicalTransferTypes';

const schema = z.object({
  title: z.string().optional(),
  registrationNo: z.string().optional(),
});

const defaultValues = {
  title: '',
  registrationNo: '',
};

interface TechnologicalTransferSearchClaimableProps {
  useSearchTechnologicalTransferHook?: () => UseMutationResult<
    TechnologicalTransferReclaimSearch,
    Error,
    TechnologicalTransferSearchRequest,
    unknown
  >;
}

export function TechnologicalTransferSearchClaimable(props: TechnologicalTransferSearchClaimableProps) {
  const { useSearchTechnologicalTransferHook = useSearchTechnologicalTransferByIdentifiers } = props;

  const form = useForm<TechnologicalTransferSearchRequest>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onSubmit',
  });
  const { t } = useTranslation();

  const { mutate, data, isPending, reset } = useSearchTechnologicalTransferHook();

  const notification = useAppNotification();
  const onSubmit = (values: TechnologicalTransferSearchRequest) => {
    // Only submit if at least one field has a value
    const hasValue = values.title?.trim() || values.registrationNo?.trim();
    if (hasValue) {
      mutate(values, {
        onError: notification.error,
        onSuccess: notification.success,
      });
    }
  };

  const reclaimMutation = useReclaimAuthorship();
  const isSubmitting = isPending || reclaimMutation.isPending;

  const navigate = useNavigate();
  const onReclaimAuthorship = (request: ReclaimRequest) => {
    reset();
    reclaimMutation.mutate(request, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        navigate({ to: `../${request.technologicalTransferId}` });
      },
    });
  };

  return (
    <Content type="full">
      <Text p="md">{t('technologicalTransfers.searchInfo')}</Text>
      {data && 'technologicalTransfer' in data && (
        <TechnologicalTransferFoundModal
          close={reset}
          data={data}
          onReclaimAuthorship={onReclaimAuthorship}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap="xs">
            <TextInputControl<TechnologicalTransferSearchRequest>
              name="title"
              leftSectionWidth="60px"
              leftSection={<Text fw="500">Title:</Text>}
              label={t('technologicalTransfers.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<TechnologicalTransferSearchRequest>
              name="registrationNo"
              leftSectionWidth="120px"
              leftSection={<Text fw="500">Register No:</Text>}
              label={t('technologicalTransfers.form.fields.registrationNo')}
              isSubmitting={isSubmitting}
            />
            <Center>
              <Button
                loading={isSubmitting}
                type="submit"
              >
                {t('button.search')}
              </Button>
            </Center>
          </Stack>
        </form>
      </FormProvider>
      <Divider />
      <Text
        c="dimmed"
        fs="italic"
        mt="10px"
      >
        {t('technologicalTransfers.addNewDisclaimer')}
      </Text>
      {!isSubmitting && (
        <AddButtonLink
          to="../create"
          name={t('button.addNew')}
        />
      )}
    </Content>
  );
}
